import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
	Lock,
	User,
	Mail,
	Shield,
	CheckCircle,
	XCircle,
	Loader2,
} from "lucide-react";
import z from "zod";
import {
	confirmInvitationMutation,
	validateInvitationOptions,
} from "@/lib/client/@tanstack/react-query.gen";

export const Route = createFileRoute("/confirm-invitation")({
	component: ConfirmInvitationPage,
	validateSearch: z.object({
		token: z.string(),
	}),
});

function ConfirmInvitationPage() {
	const navigate = useNavigate();
	const { token } = Route.useSearch();
	const [accountCreated, setAccountCreated] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");

	const {
		data: invitationData,
		isLoading,
		error,
	} = useQuery({
		...validateInvitationOptions({
			path: {
				token,
			},
		}),
		enabled: !!token,
	});

	const mutation = useMutation({
		...confirmInvitationMutation(),
		onSuccess: () => {
			setAccountCreated(true);
			setTimeout(() => {
				navigate({ to: "/login" });
			}, 3000);
		},
	});

	const form = useForm({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		onSubmit: async ({ value }) => {
			if (value.password !== value.confirmPassword) {
				return;
			}
			mutation.mutate({
				body: {
					password: value.password,
					token,
				},
			});
		},
	});

	if (isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardContent className="pt-6">
						<div className="flex flex-col items-center space-y-4">
							<Loader2 className="h-8 w-8 animate-spin text-blue-600" />
							<p className="text-gray-600">Validation de l'invitation...</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	if (error || !invitationData?.valid) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardHeader>
						<div className="flex justify-center mb-4">
							<div className="bg-red-100 p-3 rounded-full">
								<XCircle className="w-8 h-8 text-red-600" />
							</div>
						</div>
						<CardTitle className="text-center">Invitation invalide</CardTitle>
						<CardDescription className="text-center">
							{invitationData?.message ||
								"Ce lien d'invitation n'est pas valide ou a expiré."}
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	if (accountCreated) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
				<Card className="w-full max-w-md">
					<CardHeader>
						<div className="flex justify-center mb-4">
							<div className="bg-green-100 p-3 rounded-full">
								<CheckCircle className="w-8 h-8 text-green-600" />
							</div>
						</div>
						<CardTitle className="text-center">
							Compte créé avec succès !
						</CardTitle>
						<CardDescription className="text-center">
							Vous allez être redirigé vers la page de connexion...
						</CardDescription>
					</CardHeader>
				</Card>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<div className="flex justify-center mb-4">
						<div className="bg-blue-100 p-3 rounded-full">
							<Shield className="w-8 h-8 text-blue-600" />
						</div>
					</div>
					<CardTitle className="text-center">Bienvenue sur Revacours</CardTitle>
					<CardDescription className="text-center">
						Créez votre mot de passe pour activer votre compte administrateur
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4 mb-6">
						<div className="bg-gray-50 p-4 rounded-lg space-y-2">
							<div className="flex items-center gap-2 text-sm">
								<User className="h-4 w-4 text-gray-500" />
								<span className="text-gray-600">Nom :</span>
								<span className="font-medium">
									{invitationData?.firstName} {invitationData?.lastName}
								</span>
							</div>
							<div className="flex items-center gap-2 text-sm">
								<Mail className="h-4 w-4 text-gray-500" />
								<span className="text-gray-600">Email :</span>
								<span className="font-medium">{invitationData?.email}</span>
							</div>
							<div className="flex items-center gap-2 text-sm">
								<Shield className="h-4 w-4 text-gray-500" />
								<span className="text-gray-600">Rôle :</span>
								<span className="font-medium">{invitationData?.role}</span>
							</div>
						</div>
					</div>

					<form
						onSubmit={(e) => {
							e.preventDefault();
							e.stopPropagation();
							form.handleSubmit();
						}}
						className="space-y-4"
					>
						<form.Field
							name="password"
							children={(field) => (
								<div className="space-y-2">
									<Label htmlFor="password">Mot de passe</Label>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											id="password"
											type="password"
											placeholder="Minimum 8 caractères"
											className="pl-10"
											value={field.state.value}
											onChange={(e) => {
												field.handleChange(e.target.value);
												setPassword(e.target.value);
											}}
											required
											minLength={8}
										/>
									</div>
								</div>
							)}
						/>

						<form.Field
							name="confirmPassword"
							children={(field) => (
								<div className="space-y-2">
									<Label htmlFor="confirmPassword">
										Confirmer le mot de passe
									</Label>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
										<Input
											id="confirmPassword"
											type="password"
											placeholder="Confirmez votre mot de passe"
											className="pl-10"
											value={field.state.value}
											onChange={(e) => {
												field.handleChange(e.target.value);
												setConfirmPassword(e.target.value);
											}}
											required
										/>
									</div>
									{confirmPassword &&
										password &&
										confirmPassword !== password && (
											<p className="text-sm text-red-600">
												Les mots de passe ne correspondent pas
											</p>
										)}
								</div>
							)}
						/>

						{mutation.isError && (
							<Alert variant="destructive">
								<AlertDescription>
									Une erreur est survenue. Veuillez réessayer.
								</AlertDescription>
							</Alert>
						)}

						<Button
							type="submit"
							className="w-full"
							disabled={
								mutation.isPending ||
								!password ||
								!confirmPassword ||
								password !== confirmPassword ||
								password.length < 8
							}
						>
							{mutation.isPending
								? "Création du compte..."
								: "Créer mon compte"}
						</Button>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
